import type { DesignerDocument, TokenOverride, TokenPath } from '../types.js';
import { createDocument } from '../types.js';

/**
 * Serializable representation of a DesignerDocument.
 * This is the JSON format written to / read from disk.
 */
export interface SavedDocument {
  /** Format version for forward compatibility. */
  readonly formatVersion: 1;
  /** ISO-8601 timestamp of when the document was saved. */
  readonly savedAt: string;
  /** Array of active token overrides. */
  readonly overrides: readonly SavedOverride[];
}

/** Serializable representation of a single override. */
export interface SavedOverride {
  readonly group: string;
  readonly key: string;
  readonly cssVar: string;
  readonly value: string;
  readonly originalValue: string;
}

/**
 * Serialize a DesignerDocument to a SavedDocument suitable for JSON.stringify().
 */
export function saveDocument(doc: DesignerDocument): SavedDocument {
  const overrides: SavedOverride[] = [];
  for (const override of doc.overrides.values()) {
    overrides.push({
      group: override.path.group,
      key: override.path.key,
      cssVar: override.path.cssVar,
      value: override.value,
      originalValue: override.originalValue,
    });
  }
  // Sort by cssVar for deterministic output
  overrides.sort((a, b) => a.cssVar.localeCompare(b.cssVar));

  return {
    formatVersion: 1,
    savedAt: new Date().toISOString(),
    overrides,
  };
}

/**
 * Deserialize a SavedDocument back into a DesignerDocument.
 * Throws if the input is malformed.
 */
export function loadDocument(saved: unknown): DesignerDocument {
  if (saved === null || typeof saved !== 'object') {
    throw new Error('Invalid saved document: expected an object');
  }

  const obj = saved as Record<string, unknown>;

  if (obj.formatVersion !== 1) {
    throw new Error(
      `Unsupported format version: ${String(obj.formatVersion)} (expected 1)`,
    );
  }

  if (!Array.isArray(obj.overrides)) {
    throw new Error('Invalid saved document: overrides must be an array');
  }

  // BT-02: Cap override count to prevent DoS via oversized arrays
  const MAX_OVERRIDES = 500;
  if (obj.overrides.length > MAX_OVERRIDES) {
    throw new Error(
      `Too many overrides: ${obj.overrides.length} (maximum: ${MAX_OVERRIDES})`,
    );
  }

  // BT-04: CSS custom property name format
  const CSS_VAR_PATTERN = /^--[a-zA-Z][a-zA-Z0-9-]*$/;
  // BT-03: Forbidden characters in values (CSS injection prevention)
  const FORBIDDEN_VALUE_CHARS = /[{};<>]/;

  const overrides = new Map<string, TokenOverride>();

  for (let i = 0; i < obj.overrides.length; i++) {
    const entry = obj.overrides[i];
    if (entry === null || typeof entry !== 'object') {
      throw new Error(`Invalid override at index ${i}: expected an object`);
    }

    const raw = entry as Record<string, unknown>;
    const missing = (['group', 'key', 'cssVar', 'value', 'originalValue'] as const)
      .filter((f) => typeof raw[f] !== 'string');

    if (missing.length > 0) {
      throw new Error(
        `Invalid override at index ${i}: missing or non-string fields: ${missing.join(', ')}`,
      );
    }

    // BT-04: Validate cssVar format
    if (!CSS_VAR_PATTERN.test(raw.cssVar as string)) {
      throw new Error(
        `Invalid cssVar at index ${i}: "${raw.cssVar}" does not match --name format`,
      );
    }

    // BT-03: Reject values with CSS-injection characters
    if (FORBIDDEN_VALUE_CHARS.test(raw.value as string)) {
      throw new Error(
        `Invalid override value at index ${i}: contains forbidden characters`,
      );
    }

    const path: TokenPath = {
      group: raw.group as string,
      key: raw.key as string,
      cssVar: raw.cssVar as string,
    };

    overrides.set(path.cssVar, {
      path,
      value: raw.value as string,
      originalValue: raw.originalValue as string,
    });
  }

  return { overrides, version: 0 };
}

/**
 * Convenience: serialize a DesignerDocument to a JSON string.
 */
export function saveToJSON(doc: DesignerDocument): string {
  return JSON.stringify(saveDocument(doc), null, 2);
}

/**
 * Convenience: parse a JSON string into a DesignerDocument.
 * Throws on invalid JSON or invalid document structure.
 */
export function loadFromJSON(json: string): DesignerDocument {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error('Invalid JSON');
  }
  return loadDocument(parsed);
}
